import React, { ChangeEvent } from 'react';
import {Dialog, Button, RaymanUser, UserSize, TextField} from 'components/core';
import {Divider, Link} from '@material-ui/core';
import {TimersCtx} from 'components/dataProviders/tasks.reports.timers';
import {Add} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import {MsToTimeBlock} from './timers';

const useStyles = makeStyles((theme) => ({
  dialog:
  {
    width: 700
  },
  timeRow: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    margin: "5px 0"
  },
  timerBlock: {
    display: "flex",
    flexDirection: "column"
  },
  link: {
    cursor: "pointer",
    alignSelf: "flex-end"
  },
  horizontalSeparator: {
    margin: "0 20px",
    lineHeight: "40px"
  },
  verticalSeparator: {
    margin: "10px 0"
  }
}), {name: "EditTimerDialog"});

interface TimerDialogProps {
  open: boolean,
  handleClose: () => void,
  userID: number
}

interface APITimer {
  timeRecordID?: number
  userID: number,
  startDatetime: string,
  endDatetime: string
}

interface InternalTimer extends APITimer {
  totalTime: number,
  updated: boolean,
  deleted: boolean
}

interface UserTimers {
  total: number,
  timers: InternalTimer[]
}

export default function TimerDialog({open, handleClose=()=>{}, userID} : TimerDialogProps)
{
    const classes = useStyles();
  
    const ctx = React.useContext(TimersCtx);
    const [userTimers, setUserTimers] = React.useState<UserTimers>({total: 0, timers: []});

    const updateTimers = React.useCallback(() => {
      if(ctx.loading) return;
      
      let tmpUserTimers : UserTimers = {
        total: 0,
        timers: []
      };

      // Group timers by user ID
      ctx.data.array.forEach((timer : APITimer) => {
        if(timer.userID !== userID) return;

        let newTimer : InternalTimer = {...timer, totalTime: 0, updated: false, deleted: false};
        if(!newTimer.endDatetime) newTimer.endDatetime = new Date().toISOString();
        newTimer.totalTime = new Date(newTimer.endDatetime).getTime() - new Date(newTimer.startDatetime).getTime();

        tmpUserTimers.timers.push(newTimer);
        tmpUserTimers.total += newTimer.totalTime;
      });

      setUserTimers(tmpUserTimers);
    }, [ctx, userID]);

    React.useEffect(updateTimers, [ctx, userID]);


    const [loading, setLoading] = React.useState(false);

    const onClose = () => {
        updateTimers();
        handleClose();
    }
    const onConfirm = () => {
      setLoading(true);
      console.log(userTimers);
      Promise.all(userTimers.timers.map(async (timer) => {

        // ignore timers not modified
        if(!timer.updated && !timer.deleted && timer.timeRecordID) return;

        if(timer.deleted && timer.timeRecordID)
        {
          await ctx.funcs.delete(timer.timeRecordID);
        }
        else if(timer.timeRecordID)
        {
          await ctx.funcs.update(timer.timeRecordID, {startDatetime: timer.startDatetime, endDatetime: timer.endDatetime, userID: userID});
        }
        else if(!timer.timeRecordID && !timer.deleted){
          await ctx.funcs.create({startDatetime: timer.startDatetime, endDatetime: timer.endDatetime, userID: userID});
        }

      })).then(_ => {
        setLoading(false);
        onClose();
      });
    }

    function handleBlockDelete(index : number)
    {
        let updData = {...userTimers};
        updData.total -= updData.timers[index].totalTime;
        updData.timers[index].deleted = true;

        setUserTimers(updData);
    }

    function handleBlockUpdate(index: number, timer: InternalTimer)
    {
        let updData = {...userTimers};
        updData.total -= updData.timers[index].totalTime;
        timer.totalTime = new Date(timer.endDatetime).getTime() - new Date(timer.startDatetime).getTime();
        updData.total += timer.totalTime;
        timer.updated = true;
        updData.timers[index] = timer;

        setUserTimers(updData);
    }

    function handleAddBlock()
    {
        let updData = {...userTimers};
        updData.timers.push({userID: userID, totalTime: 0, updated: true, deleted: false, startDatetime: new Date().toISOString(), endDatetime: new Date().toISOString()});

        setUserTimers(updData);
    }

    return <Dialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={"Editar Tiempo"}
      subtitle={<div className={classes.timeRow}><RaymanUser size={UserSize.size32} userID={userID}/> <MsToTimeBlock time={userTimers.total} /></div>}
      confirm="Guardar"
      cancel="Cancelar"
      loading={loading}
      className={classes.dialog}
      >
      {
        userTimers.timers.map((timer, index) => timer.deleted ? null : <React.Fragment key={index}>
          <TimeBlock index={index} timer={timer} onDelete={handleBlockDelete} onChange={handleBlockUpdate} />
          <Divider className={classes.verticalSeparator} />
        </React.Fragment>)
      }
        <Button onClick={handleAddBlock}><Add />Crear bloque de tiempo</Button>
      </Dialog>
}

interface TimeBlockProps {
  index: number,
  timer: InternalTimer,
  onDelete: (index: number) => void,
  onChange: (index: number, timer: InternalTimer) => void
}

function TimeBlock({index, timer, onDelete, onChange} : TimeBlockProps)
{
  const classes = useStyles();

  const [data, setData] = React.useState(timer);

  React.useEffect(function updateData() {
    setData(timer);
  }, [timer]);

  function handleChangeStart(evt : ChangeEvent<{value: string}>)
  {
    onChange(index, {...data, startDatetime: new Date(evt.target.value).toISOString()});
  }

  function handleChangeEnd(evt : ChangeEvent<{value: string}>)
  {
    onChange(index, {...data, endDatetime: new Date(evt.target.value).toISOString()});
  }

  if(data.deleted || !data) return null;

  return <div className={classes.timerBlock}>
    <div className={classes.timeRow}>
      <MsToTimeBlock time={timer.totalTime} />
      <Link className={classes.link} onClick={() => onDelete(index)}>Eliminar</Link>
    </div>
    <div className={classes.timeRow}>
      <TextField type="datetime-local" value={ISOStringToInput(data.startDatetime)} onChange={handleChangeStart}/>
      <div className={classes.horizontalSeparator}>-</div>
      <TextField type="datetime-local" value={ISOStringToInput(data.endDatetime)} onChange={handleChangeEnd}/>
    </div>
  </div>
}


function DateToInput(date : Date)
{
  let dateTimeLocalValue = (new Date(date.getTime() - date.getTimezoneOffset() * 60000)).toISOString().slice(0, -5);
  return dateTimeLocalValue;
}

function ISOStringToInput(s : string)
{
  return DateToInput(new Date(s));
}
